// src/pages/PdeDetails.js

import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import API_BASE_URL from "../utilities/ApiConfig";
import { toast } from "react-toastify";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Stepper,
  Step,
  StepLabel,
  Typography,
  Checkbox,
  FormControlLabel,
  TextField,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import UserMenu from "../components/UserMenu";
import SideMenu from "../components/SideMenu";
import Header from "../components/Header";

/**
 * Utility: format ISO date to dd/MM/yyyy
 */
const formatDate = (isoString) => {
  if (!isoString) return "";
  const d = new Date(isoString);
  return d.toLocaleDateString("en-GB");
};

function PropertyDetailsTab({ evaluationRow }) {
  const e = evaluationRow || {};

  //
  // ─── 1) “PROPERTY DETAILS” FIELDS (THREE‐COLUMN GROUPS) ───────────────────────────
  //
  // We remove the colon from each label string (so we can render “:” in its own Grid cell).
  //
  const leftColumn = [
    ["DCode", e.dcode || "—"],
    ["SRO Code", e.srocode || "—"],
    ["Registration Year", e.regyear || "—"],
    ["Book No.", e.bookno ?? "—"],
    ["Deed Code", e.deedcode || "—"],
    ["Deed Subcode", e.deedsubcode || "—"],
    ["Location", e.location || "—"],
    ["Pasting", e.pasting ?? "—"],
    ["Pages", e.page ?? "—"],
    ["Words", e.word ?? "—"],
  ];

  const middleColumn = [
    ["Area", e.area || "—"],
    ["Transaction Value", e.transvalue || "—"],
    ["Land Cost", e.landcost || "—"],
    ["Structure Cost", e.structcost || "—"],
    ["Stamp Duty", e.stampduty || "—"],
    ["Registration Fee", e.rfee || "—"],
    ["Paid Stamp Duty", e.paiD_STAMPDUTY || "—"],
    ["Paid Registration Fee", e.paiD_RFEE || "—"],
    ["Deed Writer Name", e.dwrname || "—"],
    ["Execution Date", formatDate(e.exedate) || "—"],
  ];

  const rightColumn = [
    ["Khewat No.", e.parganacode || "—"],
    ["Khasra No.", e.khasra || "—"],
    ["Area / Unit", e.unit || "—"],
    ["Property Latitude", e.latitude || "—"],
    ["RIDer", e.vcode || "—"],
    ["Sub-District", e.dcode || "—"],
    ["Sub-Article / Sub-Deed type", e.deedsubcode || "—"],
    ["Circle Rate", e.segccode || "—"],
    ["Select Floor", e.selected_floor || "—"],
    ["House/Flat No.", e.floorno || "—"],
  ];

  //
  // ─── 2) “PROPERTY BOUNDARIES” FIELDS ──────────────────────────────────────────────
  //
  const boundaries = [
    ["East", e.east || "—"],
    ["West", e.west || "—"],
    ["North", e.north || "—"],
    ["South", e.south || "—"],
  ];

  return (
    <Box
      sx={{
        bgcolor: "#ffffff",
        p: 3,
        mt: 2,
      }}
    >
      <Grid container spacing={2}>
        {/*
          ─── LEFT COLUMN (xs=12 md=4) ────────────────────────────────────
          Each row: [Label (5/12)] [Colon (1/12)] [Value (6/12)]
        */}
        <Grid item xs={12} md={4}>
          <Grid container rowSpacing={1} columnSpacing={1}>
            {leftColumn.map(([label, value], idx) => (
              <React.Fragment key={idx}>
                {/* Label cell */}
                <Grid item xs={5}>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 500, color: "#424242" }}
                  >
                    {label}
                  </Typography>
                </Grid>
                {/* Colon cell */}
                <Grid item xs={1}>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 500, color: "#424242" }}
                  >
                    :
                  </Typography>
                </Grid>
                {/* Value cell */}
                <Grid item xs={6}>
                  <Typography variant="body2" sx={{ color: "#424242" }}>
                    {value}
                  </Typography>
                </Grid>
              </React.Fragment>
            ))}
          </Grid>
        </Grid>

        {/*
          ─── MIDDLE COLUMN (xs=12 md=4) ─────────────────────────────────
        */}
        <Grid item xs={12} md={4}>
          <Grid container rowSpacing={1} columnSpacing={1}>
            {middleColumn.map(([label, value], idx) => (
              <React.Fragment key={idx}>
                {/* Label cell */}
                <Grid item xs={5}>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 500, color: "#424242" }}
                  >
                    {label}
                  </Typography>
                </Grid>
                {/* Colon cell */}
                <Grid item xs={1}>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 500, color: "#424242" }}
                  >
                    :
                  </Typography>
                </Grid>
                {/* Value cell */}
                <Grid item xs={6}>
                  <Typography variant="body2" sx={{ color: "#424242" }}>
                    {value}
                  </Typography>
                </Grid>
              </React.Fragment>
            ))}
          </Grid>
        </Grid>

        {/*
          ─── RIGHT COLUMN (xs=12 md=4) ──────────────────────────────────
        */}
        <Grid item xs={12} md={4}>
          <Grid container rowSpacing={1} columnSpacing={1}>
            {rightColumn.map(([label, value], idx) => (
              <React.Fragment key={idx}>
                {/* Label cell */}
                <Grid item xs={5}>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 500, color: "#424242" }}
                  >
                    {label}
                  </Typography>
                </Grid>
                {/* Colon cell */}
                <Grid item xs={1}>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 500, color: "#424242" }}
                  >
                    :
                  </Typography>
                </Grid>
                {/* Value cell */}
                <Grid item xs={6}>
                  <Typography variant="body2" sx={{ color: "#424242" }}>
                    {value}
                  </Typography>
                </Grid>
              </React.Fragment>
            ))}
          </Grid>
        </Grid>

        {/*
          ─── DIVIDER & LIGHT‐GRAY “PROPERTY BOUNDARIES” BAR ─────────────────────────
        */}
        <Grid item xs={12} sx={{ mt: 2 }}>
          <Box
            sx={{
              bgcolor: "#F0F0F0",
              py: 1,
              px: 2,
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, color: "#424242" }}
            >
              Property Boundaries
            </Typography>
          </Box>
        </Grid>

        {/*
          ─── FOUR “PROPERTY BOUNDARIES” ROWS ────────────────────────────────────
          Each row: [Label (xs=4 sm=2)] [Colon (xs=1 sm=1)] [Value (xs=7 sm=3)]
        */}
        <Grid item xs={12} sx={{ mt: 1 }}>
          <Grid container rowSpacing={1} columnSpacing={1}>
            {boundaries.map(([label, value], idx) => (
              <React.Fragment key={idx}>
                {/* Label cell */}
                <Grid item xs={4} sm={2}>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 500, color: "#424242" }}
                  >
                    {label}
                  </Typography>
                </Grid>
                {/* Colon cell */}
                <Grid item xs={1} sm={1}>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 500, color: "#424242" }}
                  >
                    :
                  </Typography>
                </Grid>
                {/* Value cell */}
                <Grid item xs={7} sm={3}>
                  <Typography variant="body2" sx={{ color: "#424242" }}>
                    {value}
                  </Typography>
                </Grid>
              </React.Fragment>
            ))}
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
}

function PropertyEvaluationTab({ evaluationRow }) {
  const e = evaluationRow || {};

  //
  // ─── 1) TOP THREE COLUMNS: label / colon / value ───────────────────────────────
  //
  // We remove the colon from each label string (e.g. "Floor Sale" instead of "Floor Sale :"),
  // then render “:” in its own <Grid item>.
  //
  const leftColumn = [
    ["Floor Sale", e.floorsale || "—"],
    ["Floor No.", e.floorno ?? "—"],
    ["Location Circle Rate", e.location_circle_rate || "—"],
    ["Co-Owner Flag", e.co_ower_flag || "—"],
    ["Co-Owner No.", e.cO_OWNER_NO ?? "—"],
    ["Army Flag", e.army_flag || "—"],
    ["Disable Flag", e.disable_flag || "—"],
  ];

  const middleColumn = [
    ["Area", e.area ?? "—"],
    ["Transaction Value", e.transvalue ?? "—"],
    ["Land Cost", e.landcost ?? "—"],
    ["Structure Cost", e.structcost ?? "—"],
    ["Stamp Duty", e.stampduty ?? "—"],
    ["Registration Fee", e.rfee ?? "—"],
    ["Paid Stamp Duty", e.paiD_STAMPDUTY ?? "—"],
    ["Paid Registration Fee", e.paiD_RFEE ?? "—"],
    ["Deed Writer Name", e.dwrname || "—"],
    ["Execution Date", formatDate(e.exedate) || "—"],
  ];

  const rightColumn = [
    ["Mobileno", e.mobileno || "—"],
    ["Latitude", e.latitude ?? "—"],
    ["Longitude", e.longitude ?? "—"],
    ["Applicant Email", e.applicantEmail || "—"],
    ["Mutation Dept.", e.mutation_department || "—"],
    ["Mutation Office Code", e.mutation_office_code || "—"],
    ["Ref Dafa60", e.ref_Dafa60 || "—"],
    ["Selected Floor", e.selected_floor ?? "—"],
    ["Rider Code", e.rider_code || "—"],
    ["Appointment Type", e.appointment_type || "—"],
  ];

  return (
    <Box
      sx={{
        bgcolor: "#ffffff",
        p: 3,
        mt: 2,
      }}
    >
      <Grid container spacing={2}>
        {/* ─── LEFT COLUMN ────────────────────────────────────────────── */}
        <Grid item xs={12} md={4}>
          <Grid container rowSpacing={1} columnSpacing={1}>
            {leftColumn.map(([label, value], idx) => (
              <React.Fragment key={idx}>
                {/* Label cell (xs=5 of this column) */}
                <Grid item xs={5}>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 500, color: "#424242" }}
                  >
                    {label}
                  </Typography>
                </Grid>
                {/* Colon cell (xs=1) */}
                <Grid item xs={1}>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 500, color: "#424242" }}
                  >
                    :
                  </Typography>
                </Grid>
                {/* Value cell (xs=6) */}
                <Grid item xs={6}>
                  <Typography variant="body2" sx={{ color: "#424242" }}>
                    {value}
                  </Typography>
                </Grid>
              </React.Fragment>
            ))}
          </Grid>
        </Grid>

        {/* ─── MIDDLE COLUMN ──────────────────────────────────────────── */}
        <Grid item xs={12} md={4}>
          <Grid container rowSpacing={1} columnSpacing={1}>
            {middleColumn.map(([label, value], idx) => (
              <React.Fragment key={idx}>
                {/* Label */}
                <Grid item xs={5}>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 500, color: "#424242" }}
                  >
                    {label}
                  </Typography>
                </Grid>
                {/* Colon */}
                <Grid item xs={1}>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 500, color: "#424242" }}
                  >
                    :
                  </Typography>
                </Grid>
                {/* Value */}
                <Grid item xs={6}>
                  <Typography variant="body2" sx={{ color: "#424242" }}>
                    {value}
                  </Typography>
                </Grid>
              </React.Fragment>
            ))}
          </Grid>
        </Grid>

        {/* ─── RIGHT COLUMN ───────────────────────────────────────────── */}
        <Grid item xs={12} md={4}>
          <Grid container rowSpacing={1} columnSpacing={1}>
            {rightColumn.map(([label, value], idx) => (
              <React.Fragment key={idx}>
                {/* Label */}
                <Grid item xs={5}>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 500, color: "#424242" }}
                  >
                    {label}
                  </Typography>
                </Grid>
                {/* Colon */}
                <Grid item xs={1}>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 500, color: "#424242" }}
                  >
                    :
                  </Typography>
                </Grid>
                {/* Value */}
                <Grid item xs={6}>
                  <Typography variant="body2" sx={{ color: "#424242" }}>
                    {value}
                  </Typography>
                </Grid>
              </React.Fragment>
            ))}
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
}

// function PartyDetailsTab({ partyRows }) {
//   if (!partyRows.length) {
//     return (
//       <Typography color="error" mt={2}>
//         No party details found for this PDE.
//       </Typography>
//     );
//   }

//   const formatDate = (isoString) => {
//     if (!isoString) return "";
//     const d = new Date(isoString);
//     return d.toLocaleDateString("en-GB");
//   };

//   return (
//     <Box
//       sx={{
//         bgcolor: "#ffffff",
//         p: 3,
//         mt: 2,
//       }}
//     >
//       <Grid container spacing={2}>
//         {partyRows.map((p, idx) => {
//           // Determine section header
//           let headerText = "";
//           if (idx === 0) headerText = "First Party Details";
//           else if (idx === 1) headerText = "Second Party Details";
//           else headerText = `Witness Details ${idx - 1}`;

//           // PERSONAL DETAILS (first group of fields)
//           const personalFields = [
//             ["Pcode", p.pcode || "—"],
//             ["Name (Hindi)", p.name_Hin || "—"],
//             ["Name (English)", p.name_Eng || "—"],
//             ["DOB", formatDate(p.dob) || "—"],
//             ["Gender Code", p.gendeR_CODE || "—"],
//             ["Relation", p.relation || "—"],
//             ["Father Name (Eng)", p.father_Name_Eng || "—"],
//             ["Country", p.country || "—"],
//           ];

//           // ADDRESS DETAILS (second group of fields)
//           const addressFields = [
//             ["State", p.state || "—"],
//             ["District", p.district || "—"],
//             ["VTC", p.vtc || "—"],
//             ["Street", p.street || "—"],
//             ["House No.", p.house_no || "—"],
//             ["Landmark", p.landmark || "—"],
//             ["Pincode", p.pincode || "—"],
//             ["Occu Code", p.occu_Code || "—"],
//             ["PAN/Form60", p.paN_Form60 || "—"],
//             ["PAN No.", p.paN_no || "—"],
//             ["Mobile No.", p.mobile_No || "—"],
//             ["ID Type Code", p.id_Type_Code || "—"],
//             ["ID No.", p.id_No || "—"],
//             ["Party Email", p.partyEmail || "—"],
//           ];

//           return (
//             <Grid
//               item
//               xs={12}
//               key={idx}
//               sx={{ mb: idx < partyRows.length - 1 ? 2 : 0 }}
//             >
//               {/* Section Header */}
//               <Box
//                 sx={{
//                   bgcolor: "#F0F0F0",
//                   py: 1,
//                   px: 2,
//                   mb: 1,
//                 }}
//               >
//                 <Typography
//                   variant="subtitle1"
//                   sx={{ fontWeight: 600, color: "#424242" }}
//                 >
//                   {headerText}
//                 </Typography>
//               </Box>

//               {/* Personal Details */}
//               <Grid container spacing={2}>
//                 <Grid item xs={12} md={6}>
//                   <Grid container rowSpacing={1} columnSpacing={1}>
//                     {personalFields.map(([label, value], i) => (
//                       <React.Fragment key={i}>
//                         {/* Label cell */}
//                         <Grid item xs={5}>
//                           <Typography
//                             variant="body2"
//                             sx={{ fontWeight: 500, color: "#424242" }}
//                           >
//                             {label}
//                           </Typography>
//                         </Grid>
//                         {/* Colon cell */}
//                         <Grid item xs={1}>
//                           <Typography
//                             variant="body2"
//                             sx={{ fontWeight: 500, color: "#424242" }}
//                           >
//                             :
//                           </Typography>
//                         </Grid>
//                         {/* Value cell */}
//                         <Grid item xs={6}>
//                           <Typography variant="body2" sx={{ color: "#424242" }}>
//                             {value}
//                           </Typography>
//                         </Grid>
//                       </React.Fragment>
//                     ))}
//                   </Grid>
//                 </Grid>

//                 <Grid item xs={12} md={6}>
//                   <Grid container rowSpacing={1} columnSpacing={1}>
//                     {personalFields.slice(4).map(([label, value], i) => (
//                       <React.Fragment key={i + 4}>
//                         {/* Label cell */}
//                         <Grid item xs={5}>
//                           <Typography
//                             variant="body2"
//                             sx={{ fontWeight: 500, color: "#424242" }}
//                           >
//                             {personalFields[i + 4][0]}
//                           </Typography>
//                         </Grid>
//                         {/* Colon cell */}
//                         <Grid item xs={1}>
//                           <Typography
//                             variant="body2"
//                             sx={{ fontWeight: 500, color: "#424242" }}
//                           >
//                             :
//                           </Typography>
//                         </Grid>
//                         {/* Value cell */}
//                         <Grid item xs={6}>
//                           <Typography variant="body2" sx={{ color: "#424242" }}>
//                             {personalFields[i + 4][1]}
//                           </Typography>
//                         </Grid>
//                       </React.Fragment>
//                     ))}
//                   </Grid>
//                 </Grid>
//               </Grid>

//               {/* Address Details Subheader */}
//               <Box sx={{ mt: 2, mb: 1 }}>
//                 <Typography
//                   variant="subtitle2"
//                   sx={{ fontWeight: 600, color: "#424242" }}
//                 >
//                   Address details
//                 </Typography>
//               </Box>

//               {/* Address Details */}
//               <Grid container spacing={2}>
//                 <Grid item xs={12} md={6}>
//                   <Grid container rowSpacing={1} columnSpacing={1}>
//                     {addressFields.slice(0, 7).map(([label, value], i) => (
//                       <React.Fragment key={i}>
//                         {/* Label cell */}
//                         <Grid item xs={5}>
//                           <Typography
//                             variant="body2"
//                             sx={{ fontWeight: 500, color: "#424242" }}
//                           >
//                             {label}
//                           </Typography>
//                         </Grid>
//                         {/* Colon cell */}
//                         <Grid item xs={1}>
//                           <Typography
//                             variant="body2"
//                             sx={{ fontWeight: 500, color: "#424242" }}
//                           >
//                             :
//                           </Typography>
//                         </Grid>
//                         {/* Value cell */}
//                         <Grid item xs={6}>
//                           <Typography variant="body2" sx={{ color: "#424242" }}>
//                             {value}
//                           </Typography>
//                         </Grid>
//                       </React.Fragment>
//                     ))}
//                   </Grid>
//                 </Grid>

//                 <Grid item xs={12} md={6}>
//                   <Grid container rowSpacing={1} columnSpacing={1}>
//                     {addressFields.slice(7).map(([label, value], i) => (
//                       <React.Fragment key={i + 7}>
//                         {/* Label cell */}
//                         <Grid item xs={5}>
//                           <Typography
//                             variant="body2"
//                             sx={{ fontWeight: 500, color: "#424242" }}
//                           >
//                             {addressFields[i + 7][0]}
//                           </Typography>
//                         </Grid>
//                         {/* Colon cell */}
//                         <Grid item xs={1}>
//                           <Typography
//                             variant="body2"
//                             sx={{ fontWeight: 500, color: "#424242" }}
//                           >
//                             :
//                           </Typography>
//                         </Grid>
//                         {/* Value cell */}
//                         <Grid item xs={6}>
//                           <Typography variant="body2" sx={{ color: "#424242" }}>
//                             {addressFields[i + 7][1]}
//                           </Typography>
//                         </Grid>
//                       </React.Fragment>
//                     ))}
//                   </Grid>
//                 </Grid>
//               </Grid>
//             </Grid>
//           );
//         })}
//       </Grid>
//     </Box>
//   );
// }

function PartyDetailsTab({ partyRows }) {
  // nothing to show
  if (!partyRows.length) {
    return (
      <Typography color="error" mt={2}>
        No party details found for this PDE.
      </Typography>
    );
  }

  // helper to format ISO → dd/MM/yyyy
  const fmtDate = (isoString) => {
    if (!isoString) return "—";
    return new Date(isoString).toLocaleDateString("en-GB");
  };

  // 1) group by pcode prefix
  const firstParties = partyRows.filter((p) => p.pcode.startsWith("01"));
  const secondParties = partyRows.filter((p) => p.pcode.startsWith("02"));
  const witnessParties = partyRows.filter((p) => p.pcode.startsWith("03"));

  const groups = [
    { title: "First Party", rows: firstParties },
    { title: "Second Party", rows: secondParties },
    { title: "Witness", rows: witnessParties },
  ];

  // 2) render one “section” for each row in each group
  return (
    <Box sx={{ bgcolor: "#ffffff", p: 3, mt: 2 }}>
      <Grid container spacing={2}>
        {groups.map(({ title, rows }) =>
          rows.map((p, idx) => {
            const headerText = `${title}${
              rows.length > 1 ? ` ${idx + 1}` : ""
            } Details`;

            // personal fields
            const personalFields = [
              ["Pcode", p.pcode],
              ["Name (Hindi)", p.name_Hin],
              ["Name (English)", p.name_Eng],
              ["DOB", fmtDate(p.dob)],
              ["Gender Code", p.gendeR_CODE],
              ["Relation", p.relation],
              ["Father Name (Eng)", p.father_Name_Eng],
              ["Country", p.country],
            ];
            // address fields
            const addressFields = [
              ["State", p.state],
              ["District", p.district],
              ["VTC", p.vtc],
              ["Street", p.street],
              ["House No.", p.house_no],
              ["Landmark", p.landmark],
              ["Pincode", p.pincode],
              ["Occu Code", p.occu_Code],
              ["PAN/Form60", p.paN_Form60],
              ["PAN No.", p.paN_no],
              ["Mobile No.", p.mobile_No],
              ["ID Type Code", p.id_Type_Code],
              ["ID No.", p.id_No],
              ["Party Email", p.partyEmail],
            ];

            return (
              <Grid item xs={12} key={`${title}-${idx}`}>
                {/* section header */}
                <Box sx={{ bgcolor: "#F0F0F0", py: 1, px: 2, mb: 1 }}>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 600, color: "#424242" }}
                  >
                    {headerText}
                  </Typography>
                </Box>

                {/* personal details */}
                <Grid container spacing={2} mb={2}>
                  {[0, 1].map((col) => (
                    <Grid item xs={12} md={6} key={col}>
                      <Grid container rowSpacing={1} columnSpacing={1}>
                        {personalFields
                          .slice(col * 4, col * 4 + 4)
                          .map(([label, value], i) => (
                            <React.Fragment key={i}>
                              <Grid item xs={5}>
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 500, color: "#424242" }}
                                >
                                  {label}
                                </Typography>
                              </Grid>
                              <Grid item xs={1}>
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 500, color: "#424242" }}
                                >
                                  :
                                </Typography>
                              </Grid>
                              <Grid item xs={6}>
                                <Typography
                                  variant="body2"
                                  sx={{ color: "#424242" }}
                                >
                                  {value ?? "—"}
                                </Typography>
                              </Grid>
                            </React.Fragment>
                          ))}
                      </Grid>
                    </Grid>
                  ))}
                </Grid>

                {/* address details header */}
                <Box sx={{ mb: 1 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 600, color: "#424242" }}
                  >
                    Address Details
                  </Typography>
                </Box>

                {/* address details */}
                <Grid container spacing={2} mb={2}>
                  {[0, 1].map((col) => (
                    <Grid item xs={12} md={6} key={col}>
                      <Grid container rowSpacing={1} columnSpacing={1}>
                        {addressFields
                          .slice(col * 7, col * 7 + 7)
                          .map(([label, value], i) => (
                            <React.Fragment key={i}>
                              <Grid item xs={5}>
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 500, color: "#424242" }}
                                >
                                  {label}
                                </Typography>
                              </Grid>
                              <Grid item xs={1}>
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 500, color: "#424242" }}
                                >
                                  :
                                </Typography>
                              </Grid>
                              <Grid item xs={6}>
                                <Typography
                                  variant="body2"
                                  sx={{ color: "#424242" }}
                                >
                                  {value ?? "—"}
                                </Typography>
                              </Grid>
                            </React.Fragment>
                          ))}
                      </Grid>
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            );
          })
        )}
      </Grid>
    </Box>
  );
}

// ─── 8) “Action” Tab Content ─────────────────────────────────────────────────
function ActionTab({
  statusRow,
  approved,
  setApproved,
  revertBack,
  setRevertBack,
  remark,
  setRemark,
  handleSubmit,
  remarkRef,
}) {
  // If statusCode !== 1, show read-only details & hide the "Complete Review" button
  const isReadOnly = statusRow?.statusCode !== 1;
  debugger;
  // Determine a human‐readable label for Approved/Reverted
  let statusText = "";
  if (statusRow?.statusCode === 2) statusText = "Approved";
  else if (statusRow?.statusCode === 3) statusText = "Reverted";
  else statusText = "Pending";

  return (
    <Box
      sx={{
        bgcolor: "#ffffff",
        // border: "1px solid #E0E0E0",
        // borderRadius: 1,
        p: 3,
        mt: 2,
      }}
    >
      <Typography variant="subtitle1" sx={{ mb: 1 }}>
        Current Status:{" "}
        <span
          style={{
            fontWeight: 600,
            color:
              statusRow?.statusCode === 2
                ? "green"
                : statusRow?.statusCode === 3
                ? "red"
                : "orange",
          }}
        >
          {statusText}
        </span>
      </Typography>

      {/* If still pending, let user choose; otherwise, show disabled checkboxes */}
      <FormControlLabel
        control={
          <Checkbox
            checked={approved}
            onChange={(e) => {
              setApproved(e.target.checked);
              if (e.target.checked) setRevertBack(false);
            }}
            disabled={isReadOnly} // disable if not pending
          />
        }
        label="Approve"
      />
      <FormControlLabel
        control={
          <Checkbox
            checked={revertBack}
            onChange={(e) => {
              setRevertBack(e.target.checked);
              if (e.target.checked) setApproved(false);
            }}
            disabled={isReadOnly}
          />
        }
        label="Revert back for correction"
      />

      <Box mt={2}>
        <Typography variant="subtitle2">Remark</Typography>
        <TextField
          placeholder="Type Something"
          value={isReadOnly ? statusRow?.remark || "" : remark}
          onChange={(e) => setRemark(e.target.value)}
          fullWidth
          multiline
          rows={4}
          inputRef={remarkRef}
          disabled={isReadOnly}
        />
      </Box>

      {/* Only show “Complete Review” if status is still pending (1) */}
      {!isReadOnly && (
        <Box mt={3}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            startIcon={<CheckIcon />}
          >
            Complete Review
          </Button>
        </Box>
      )}
    </Box>
  );
}

const PdeDetails = () => {
  const { pdeId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [statuses, setStatuses] = useState([]);
  const [evaluations, setEvaluations] = useState([]);
  const [parties, setParties] = useState([]);
  const remarkRef = useRef(null);

  /**
   * Which step is active:
   *  0 → Property Details
   *  1 → Property Evaluation
   *  2 → Party Details
   *  3 → Action
   */
  const [activeStep, setActiveStep] = useState(0);

  const [statusRow, setStatusRow] = useState(null);
  const [evaluationRow, setEvaluationRow] = useState(null);
  const [partyRows, setPartyRows] = useState([]);

  // Action‐tab state
  const [approved, setApproved] = useState(false);
  const [revertBack, setRevertBack] = useState(false);
  const [remark, setRemark] = useState("");

  const steps = [
    "1 Property Details",
    "2 Property Evaluation",
    "3 Party Details",
    "4 Action",
  ];

  // ─── 1) Fetch detail data for this PDE_ID ───────────────────────────────────
  const fetchDetailData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/ReviewPDE/GetPendingReviewPdes?pdeId=${pdeId}`
      );
      const {
        statuses = [],
        evaluations = [],
        parties = [],
      } = response.data || {};

      setStatuses(statuses);
      setEvaluations(evaluations);
      setParties(parties);

      const sRow = statuses.length ? statuses[0] : null;
      setStatusRow(sRow);

      const eRow = evaluations.length ? evaluations[0] : null;
      setEvaluationRow(eRow);

      const pRows = parties.filter((p) => p.pdE_ID === pdeId);
      setPartyRows(pRows);

      if (sRow) {
        if (sRow.statusCode === 2) {
          // Already approved → disable checkboxes
          setApproved(true);
          setRevertBack(false);
          setRemark(sRow.remark || "");
        } else if (sRow.statusCode === 3) {
          // Already reverted → disable checkboxes
          setApproved(false);
          setRevertBack(true);
          setRemark(sRow.remark || "");
        }
      }
    } catch (err) {
      console.error("Error fetching PDE detail data:", err);
      toast.error("Failed to load PDE details.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDetailData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdeId]);

  // ─── Automatically focus the remark box as soon as we enter Step 4
  useEffect(() => {
    if (activeStep === 3 && remarkRef.current) {
      remarkRef.current.focus();
    }
  }, [activeStep]);

  const handlePrev = () => {
    setActiveStep((prev) => Math.max(prev - 1, 0));
  };

  const handleNext = () => {
    setActiveStep((prev) => Math.min(prev + 1, steps.length - 1));
  };

  const handleSubmit = async () => {
    if (!approved && !revertBack) {
      toast.error("Please select Approve or Revert back for correction.");
      return;
    }
    if (revertBack && remark.trim() === "") {
      toast.error("Please enter a remark when reverting.");
      return;
    }

    const dcode = evaluationRow?.dcode || "";
    const srocode = evaluationRow?.srocode || "";

    const payload = {
      PDE_ID: pdeId,
      dcode: dcode,
      srocode: srocode,
      ReviewStatus: approved ? "Accepted" : "Reverted",
      Remark: remark,
      UpdatedBy: "currentAdminUser", // replace with actual admin username
    };

    try {
      await axios.post(`${API_BASE_URL}/ReviewPDE/review`, payload);
      toast.success("Review submitted successfully.");
      navigate("/PdeReview");
    } catch (err) {
      console.error(err);
      toast.error("Failed to submit review.");
    }
  };

  if (loading || !statusRow) {
    return (
      <Box
        height="100vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <div className="flex h-screen bg-gray-100">
      <SideMenu />

      <div className="flex-1 flex flex-col">
        <Header />

        <div className="flex flex-1 overflow-hidden">
          {/* Main scrollable area */}
          <div className="flex-1 h-full overflow-auto p-4">
            <Box>
              <Typography variant="h5" fontWeight="600">
                PDE ID: {pdeId}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Status:{" "}
                {statusRow.statusCode === 1
                  ? "Pending"
                  : statusRow.statusCode === 2
                  ? "Approved"
                  : "Reverted"}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Last Updated: {formatDate(statusRow.updatedOn)}
              </Typography>
            </Box>
            {/* Stepper (Progress bar) */}
            <Box mt={4}>
              <Stepper activeStep={activeStep} alternativeLabel>
                {steps.map((label, idx) => (
                  <Step key={idx}>
                    <StepLabel>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {label}
                      </Typography>
                    </StepLabel>
                  </Step>
                ))}
              </Stepper>
            </Box>

            <Box
              sx={{
                // 1) Responsive width – you can adjust as needed:
                width: {
                  xs: "100%", // on very small screens: full width
                  sm: "95%", // on small screens/tablets: 95% of container
                  md: "90%", // on medium+ screens: 90%
                  lg: "85%", // on large: 85%
                },
                mx: "auto", // center horizontally
                mt: 3, // some top margin below stepper
                // 2) Compute total height = viewport height − (header + title + stepper + some padding).
                //    You’ll likely need to tweak the “250px” number to exactly match your own Nav+Header+Stepper height.
                height: {
                  xs: "calc(100vh - 200px)", // small mobile: subtract ~200px for header/stepper
                  sm: "calc(100vh - 220px)", // tablets: subtract ~220px
                  md: "calc(100vh - 250px)", // desktops: subtract ~250px
                  lg: "calc(100vh - 280px)", // large screens: subtract ~280px
                },
                display: "flex",
                flexDirection: "column",
                bgcolor: "#ffffff",
                // border: "1px solid #E0E0E0",
                // borderRadius: 1,
                p: 2,
              }}
            >
              <Box
                sx={{
                  flexGrow: 1,
                  overflowY: "auto",
                  pr: 1, // add a little right padding so scrollbar doesn’t cover content
                }}
              >
                {activeStep === 0 && (
                  <PropertyDetailsTab evaluationRow={evaluationRow} />
                )}
                {activeStep === 1 && (
                  <PropertyEvaluationTab evaluationRow={evaluationRow} />
                )}
                {activeStep === 2 && <PartyDetailsTab partyRows={partyRows} />}
                {activeStep === 3 && (
                  <ActionTab
                    statusRow={statusRow}
                    approved={approved}
                    setApproved={setApproved}
                    revertBack={revertBack}
                    setRevertBack={setRevertBack}
                    remark={remark}
                    setRemark={setRemark}
                    handleSubmit={handleSubmit}
                    remarkRef={remarkRef}
                  />
                )}
              </Box>

              <Box
                sx={{
                  mt: 2,
                  display: "flex",
                  justifyContent: "flex-start", // ← align buttons at the left
                  alignItems: "center",
                  gap: 1, // ← small space between the two buttons
                }}
              >
                <Button
                  variant="outlined"
                  size="medium"
                  disabled={activeStep === 0}
                  onClick={handlePrev}
                  sx={{
                    minWidth: "100px", // match screenshot’s “Prev” width
                    height: "36px", // match screenshot’s button height
                  }}
                >
                  Prev
                </Button>

                {activeStep < steps.length - 1 && (
                  <Button
                    variant="contained"
                    size="medium"
                    onClick={handleNext}
                    sx={{
                      minWidth: "100px", // match screenshot’s “Next” width
                      height: "36px", // match screenshot’s button height
                    }}
                  >
                    Next
                  </Button>
                )}
              </Box>
            </Box>
          </div>

          {/* Right‐hand user menu */}
          <div className="w-52 3xl:w-64 h-full">
            <UserMenu />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PdeDetails;
